import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';

const LessonEditor = ({ moduleId, courseId, lesson, onClose, onSave, isSubStrand = true }) => {
    const { post, patch } = useApi();
    const { showToast } = useAppState();
    const isEditMode = Boolean(lesson);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        title: '',
        code: '',
        order: 1,
    });

    useEffect(() => {
        if (lesson) {
            setFormData({
                // CBC API returns 'name'/'description' for sub-strands, not 'title'/'summary'
                title: lesson.name || lesson.title || '',
                code: lesson.code || '',
                order: lesson.order || 1,
            });
        }
    }, [lesson]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = 'Lesson title is required';
        if (formData.order < 1) newErrors.order = 'Order must be at least 1';
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                ...formData,
                module: moduleId,
            };

            if (isEditMode) {
                const url = isSubStrand ? `/api/cbc/sub-strands/${lesson.id}/` : `/api/teacher/lessons/${lesson.id}/`;
                // Use PATCH (not PUT) so only the sent fields are updated.
                // Strip 'order' and 'code' from edit payload to avoid unique constraint violations.
                const { order: _order, code: _code, ...payloadWithoutOrderAndCode } = payload;
                const finalPayload = isSubStrand ? { 
                    ...payloadWithoutOrderAndCode, 
                    name: payloadWithoutOrderAndCode.title,
                    strand: moduleId,
                } : payloadWithoutOrderAndCode;
                
                await patch(url, finalPayload);
                showToast(`${isSubStrand ? 'Sub-strand' : 'Lesson'} updated successfully!`);
            } else {
                const url = isSubStrand ? `/api/cbc/sub-strands/` : `/api/teacher/modules/${moduleId}/lessons/`;
                const finalPayload = isSubStrand ? {
                    name: payload.title,
                    order: payload.order,
                    strand: moduleId,
                    learning_area: courseId,
                    code: `SS-${Math.floor(Math.random() * 10000)}` 
                } : payload;

                await post(url, finalPayload);
                showToast(`${isSubStrand ? 'Sub-strand' : 'Lesson'} created successfully!`);
            }
            onSave();
            onClose();
        } catch (error) {
            console.error('Error saving lesson:', error);
            if (error.response?.data) {
                setErrors(error.response.data);
            } else {
                showToast('Failed to save lesson', 'error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-300">
                {/* Header */}
                <div className="px-8 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
                    <div>
                        <h2 className="text-3xl font-black text-[#18216D] tracking-tight">
                            {isEditMode ? 'Edit Sub-strand' : 'New Sub-strand'}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-3 bg-white border border-gray-200 rounded-2xl text-gray-400 hover:text-red-500 hover:border-red-100 transition-all shadow-sm"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Form Content */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
                    <div className="space-y-6">
                        <div>
                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Sub-strand Title</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="e.g. Identifying Proper Fractions"
                                className={`w-full px-5 py-4 bg-slate-50 border-2 border-transparent focus:border-[#18216D] focus:bg-white rounded-2xl transition-all font-bold text-gray-900 outline-none ${errors.title ? 'border-red-500' : ''}`}
                            />
                            {errors.title && <p className="text-red-500 text-xs mt-2 ml-1 font-bold">{errors.title}</p>}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {isEditMode && (
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Registry Code</label>
                                    <input
                                        type="text"
                                        name="code"
                                        value={formData.code}
                                        readOnly
                                        className="w-full px-5 py-4 bg-slate-100 border-2 border-transparent text-gray-500 rounded-2xl font-bold uppercase outline-none cursor-not-allowed"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Order in Strand</label>
                                <input
                                    type="number"
                                    name="order"
                                    value={formData.order}
                                    onChange={handleChange}
                                    readOnly={isEditMode}
                                    min="1"
                                    className={`w-full px-5 py-4 ${isEditMode ? 'bg-slate-100 text-gray-500 cursor-not-allowed' : 'bg-slate-50 focus:border-[#18216D] focus:bg-white text-gray-900'} border-2 border-transparent rounded-2xl transition-all font-bold outline-none`}
                                />
                            </div>
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div className="px-10 py-6 border-t border-gray-100 bg-slate-50 flex items-center justify-end gap-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-8 py-4 text-gray-500 font-black text-xs uppercase tracking-widest hover:text-gray-900 transition-colors"
                    >
                        Discard
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-10 py-5 bg-[#18216D] text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-indigo-900/20 hover:bg-[#0D164F] hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                    >
                        {submitting ? 'Authenticating...' : (isEditMode ? 'Update Lesson' : 'Publish Lesson')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LessonEditor;
